import { ErrorComponent, InlineError, LoadingIndicator } from "@/components/QueryState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { formatCostLong, formatDate, relativeIso } from "@/lib/format";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

const LEDGER_PAGE_SIZE = 25;

interface Props {
  orgId: string;
}

export function OrgBillingPanel({ orgId }: Props) {
  const queryClient = useQueryClient();
  const queryKey = ["admin-org-billing", orgId];
  const ledgerKey = ["admin-org-ledger", orgId];

  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: () => api.admin.orgBilling(orgId),
    enabled: Boolean(orgId),
  });

  const [topupAmount, setTopupAmount] = useState("");
  const [topupNote, setTopupNote] = useState("");
  const [topupLabel, setTopupLabel] = useState("");
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [markupInput, setMarkupInput] = useState("");
  const [ledgerOffset, setLedgerOffset] = useState(0);
  const [creditsOffset, setCreditsOffset] = useState(0);
  const creditsKey = ["admin-org-prepaid", orgId];

  const {
    data: ledger,
    isError: ledgerIsError,
    error: ledgerError,
  } = useQuery({
    queryKey: [...ledgerKey, ledgerOffset],
    queryFn: () => api.admin.orgLedger(orgId, LEDGER_PAGE_SIZE, ledgerOffset),
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
  });

  // Prepaid state: billing account (auto-recharge config + saved card) and
  // the money-movement ledger slice with Stripe invoice links.
  const {
    data: prepaid,
    isError: creditsIsError,
    error: creditsError,
  } = useQuery({
    queryKey: [...creditsKey, creditsOffset],
    queryFn: () => api.admin.orgPrepaid(orgId, LEDGER_PAGE_SIZE, creditsOffset),
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
  });
  const credits = prepaid?.payments;
  const account = prepaid?.account ?? null;

  const topupMutation = useMutation({
    mutationFn: (input: { amount: number; description?: string; grantLabel?: string }) =>
      api.admin.orgCreditsTopup(orgId, input.amount, input.description, input.grantLabel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ledgerKey });
      queryClient.invalidateQueries({ queryKey: creditsKey });
      queryClient.invalidateQueries({ queryKey: ["admin-organizations-enriched"] });
      setLedgerOffset(0);
      setCreditsOffset(0);
      setTopupAmount("");
      setTopupNote("");
      setTopupLabel("");
    },
  });

  const markupMutation = useMutation({
    mutationFn: (aiMarkupPercent: number | null) =>
      api.admin.orgMarkupOverride(orgId, aiMarkupPercent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      setMarkupInput("");
    },
  });

  const adjustMutation = useMutation({
    mutationFn: (input: { amount: number; reason: string }) =>
      api.admin.orgCreditsAdjust(orgId, input.amount, input.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ledgerKey });
      queryClient.invalidateQueries({ queryKey: creditsKey });
      queryClient.invalidateQueries({ queryKey: ["admin-organizations-enriched"] });
      setLedgerOffset(0);
      setCreditsOffset(0);
      setAdjustAmount("");
      setAdjustReason("");
    },
  });

  if (isLoading) return <LoadingIndicator />;
  if (isError || !data) {
    return <ErrorComponent error={error ?? "Failed to load billing"} />;
  }

  const submitTopup = () => {
    const amount = Number(topupAmount);
    if (!Number.isFinite(amount) || amount <= 0) return;
    topupMutation.mutate({
      amount,
      description: topupNote.length > 0 ? topupNote : undefined,
      grantLabel: topupLabel.trim().length > 0 ? topupLabel.trim() : undefined,
    });
  };

  const submitAdjust = () => {
    const amount = Number(adjustAmount);
    if (!Number.isFinite(amount) || amount === 0 || adjustReason.trim() === "") return;
    adjustMutation.mutate({ amount, reason: adjustReason.trim() });
  };

  const submitMarkup = () => {
    const pct = Number(markupInput);
    if (!Number.isFinite(pct) || pct < 0) return;
    markupMutation.mutate(pct);
  };

  const clearMarkup = () => markupMutation.mutate(null);

  return (
    <div className="space-y-8">
      <section className="grid gap-4 md:grid-cols-3">
        <SummaryCard label="Credit balance" value={formatCostLong(data.creditBalance)} />
        <SummaryCard
          label="Effective markup"
          value={`${data.markup.effective.toFixed(2)}%`}
          sub={
            data.markup.override !== null
              ? `Override (global: ${data.markup.global.toFixed(2)}%)`
              : `Inheriting global`
          }
        />
        <SummaryCard
          label="LLM spend (gateway, all-time)"
          value={formatCostLong(data.consumption.totalMarkedUp)}
          sub={`Raw: ${formatCostLong(data.consumption.totalRaw)} · ${data.consumption.requestCount} requests`}
        />
      </section>

      <section className="space-y-3">
        <h3 className="font-medium">Activity by period</h3>
        <div className="grid gap-4 md:grid-cols-3">
          {data.windows.map((w) => (
            <div key={w.key} className="rounded-md border p-4 space-y-3">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">
                {w.label}
              </div>
              <WindowMetric
                label="LLM spend"
                value={formatCostLong(w.spend.totalMarkedUp)}
                sub={`Raw: ${formatCostLong(w.spend.totalRaw)} · ${w.spend.requestCount} requests`}
              />
              <WindowMetric
                label="Credits consumed"
                value={formatCostLong(w.creditsConsumed)}
              />
              <WindowMetric
                label="Credits granted"
                value={formatCostLong(w.creditsGranted)}
              />
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="font-medium">Auto-recharge</h3>
        {account ? (
          <div className="rounded-md border p-4 grid gap-4 md:grid-cols-4">
            <WindowMetric
              label="Status"
              value={account.autoRecharge.enabled ? "On" : "Off"}
              sub={
                account.autoRecharge.disabledReason
                  ? `Disabled: ${account.autoRecharge.disabledReason}`
                  : account.autoRecharge.consecutiveFailures > 0
                    ? `${account.autoRecharge.consecutiveFailures} consecutive failure(s)`
                    : undefined
              }
            />
            <WindowMetric
              label="Trigger"
              value={
                account.autoRecharge.thresholdUsd !== null
                  ? `< ${formatCostLong(account.autoRecharge.thresholdUsd)}`
                  : "—"
              }
              sub={
                account.autoRecharge.amountUsd !== null
                  ? `Recharges ${formatCostLong(account.autoRecharge.amountUsd)}`
                  : undefined
              }
            />
            <WindowMetric
              label="Monthly cap"
              value={
                account.autoRecharge.monthlyCapUsd !== null
                  ? formatCostLong(account.autoRecharge.monthlyCapUsd)
                  : "No cap"
              }
              sub={`Used this month: ${formatCostLong(account.autoRecharge.monthUsedUsd)}`}
            />
            <WindowMetric
              label="Payment method"
              value={
                account.paymentMethodLast4
                  ? `${account.paymentMethodBrand ?? "Card"} •••• ${account.paymentMethodLast4}`
                  : "None saved"
              }
              sub={`${account.provider}${account.providerCustomerId ? ` · ${account.providerCustomerId}` : ""}`}
            />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No billing account yet — this org hasn't touched a payment flow
            (not in the prepaid rollout, or simply never bought credits).
          </p>
        )}
      </section>

      <section className="grid gap-6 md:grid-cols-2">
        <div className="rounded-md border p-4 space-y-3">
          <h3 className="font-medium">Add credits</h3>
          <p className="text-sm text-muted-foreground">
            Grants are written to the credit ledger as type{" "}
            <code className="font-mono text-xs">topup</code>.
          </p>
          <div className="space-y-2">
            <Label htmlFor="topup-amount" className="text-xs">
              Amount (USD)
            </Label>
            <Input
              id="topup-amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="10.00"
              value={topupAmount}
              onChange={(e) => setTopupAmount(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="topup-note" className="text-xs">
              Internal note (optional, never shown to the customer)
            </Label>
            <Input
              id="topup-note"
              placeholder="Reason for grant"
              value={topupNote}
              onChange={(e) => setTopupNote(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="topup-label" className="text-xs">
              Customer-facing label (optional) — shows as "Boo Lab Credit — …"
            </Label>
            <Input
              id="topup-label"
              placeholder="e.g. Early adopter, Referral, Goodwill"
              maxLength={40}
              value={topupLabel}
              onChange={(e) => setTopupLabel(e.target.value)}
            />
          </div>
          {topupMutation.isError && (
            <InlineError error={topupMutation.error ?? "Top-up failed"} />
          )}
          <Button
            onClick={submitTopup}
            disabled={
              topupMutation.isPending ||
              !Number.isFinite(Number(topupAmount)) ||
              Number(topupAmount) <= 0
            }
          >
            {topupMutation.isPending ? "Granting…" : "Grant credits"}
          </Button>
        </div>

        <div className="rounded-md border p-4 space-y-3">
          <h3 className="font-medium">Adjust balance</h3>
          <p className="text-sm text-muted-foreground">
            Signed correction written as type{" "}
            <code className="font-mono text-xs">adjustment</code> — use a negative
            amount to claw back credits. A reason is required for the audit log.
          </p>
          <div className="space-y-2">
            <Label htmlFor="adjust-amount" className="text-xs">
              Amount (USD, signed)
            </Label>
            <Input
              id="adjust-amount"
              type="number"
              step="0.01"
              placeholder="-5.00"
              value={adjustAmount}
              onChange={(e) => setAdjustAmount(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="adjust-reason" className="text-xs">
              Reason (required)
            </Label>
            <Input
              id="adjust-reason"
              placeholder="Why this correction is needed"
              value={adjustReason}
              onChange={(e) => setAdjustReason(e.target.value)}
            />
          </div>
          {adjustMutation.isError && (
            <InlineError error={adjustMutation.error ?? "Adjustment failed"} />
          )}
          <Button
            variant={Number(adjustAmount) < 0 ? "destructive" : "default"}
            onClick={submitAdjust}
            disabled={
              adjustMutation.isPending ||
              !Number.isFinite(Number(adjustAmount)) ||
              Number(adjustAmount) === 0 ||
              adjustReason.trim() === ""
            }
          >
            {adjustMutation.isPending ? "Adjusting…" : "Apply adjustment"}
          </Button>
        </div>

        <div className="rounded-md border p-4 space-y-3">
          <h3 className="font-medium">AI markup override</h3>
          <p className="text-sm text-muted-foreground">
            Percent applied on top of raw upstream LLM cost for this org.
            Clearing the override returns to the global default
            (currently {data.markup.global.toFixed(2)}%).
          </p>
          <div className="space-y-2">
            <Label htmlFor="markup-pct" className="text-xs">
              Override (%)
            </Label>
            <Input
              id="markup-pct"
              type="number"
              step="0.01"
              min="0"
              placeholder={
                data.markup.override !== null
                  ? `Currently ${data.markup.override}`
                  : `e.g. 25`
              }
              value={markupInput}
              onChange={(e) => setMarkupInput(e.target.value)}
            />
          </div>
          {markupMutation.isError && (
            <InlineError error={markupMutation.error ?? "Update failed"} />
          )}
          <div className="flex gap-2">
            <Button
              onClick={submitMarkup}
              disabled={
                markupMutation.isPending ||
                !Number.isFinite(Number(markupInput)) ||
                Number(markupInput) < 0
              }
            >
              {markupMutation.isPending ? "Saving…" : "Set override"}
            </Button>
            <Button
              variant="outline"
              onClick={clearMarkup}
              disabled={markupMutation.isPending || data.markup.override === null}
            >
              Clear override
            </Button>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="font-medium">
          Recent LLM cost — raw vs marked-up ({data.recentConsumption.length})
        </h3>
        {data.recentConsumption.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No gateway-recorded LLM cost yet for this org.
          </p>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Request ID</th>
                  <th className="px-3 py-2 text-right">Raw</th>
                  <th className="px-3 py-2 text-right">Marked-up</th>
                  <th className="px-3 py-2 text-right">Markup %</th>
                </tr>
              </thead>
              <tbody>
                {data.recentConsumption.map((row) => (
                  <tr key={row.gatewayRequestId} className="border-t">
                    <td className="px-3 py-2 text-muted-foreground">
                      {relativeIso(row.createdAt)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {row.gatewayRequestId.slice(0, 12)}…
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatCostLong(row.rawCostUsd)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatCostLong(row.markedUpCostUsd)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {row.markupPercent.toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="font-medium">
          Payments &amp; grants{credits ? ` (${credits.total})` : ""}
        </h3>
        {creditsIsError ? (
          <InlineError error={creditsError ?? "Failed to load payments"} />
        ) : (
          <>
            <div className="rounded-md border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Note</th>
                    <th className="px-3 py-2">By</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-right">Balance after</th>
                    <th className="px-3 py-2">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {(credits?.entries ?? []).map((row) => (
                    <tr key={row.id} className="border-t">
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                        {formatDate(row.createdAt as unknown as string)}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="secondary">{row.type}</Badge>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {row.description ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {row.createdBy ?? "—"}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums ${
                          row.amount < 0 ? "text-destructive" : "text-green-600"
                        }`}
                      >
                        {row.amount > 0 ? "+" : ""}
                        {formatCostLong(row.amount)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatCostLong(row.balanceAfter)}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {row.invoiceUrl || row.invoicePdf ? (
                          <span className="inline-flex items-center gap-3">
                            {row.invoiceUrl && (
                              <a
                                href={row.invoiceUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="text-primary underline underline-offset-4 hover:no-underline"
                              >
                                View
                              </a>
                            )}
                            {row.invoicePdf && (
                              <a
                                href={row.invoicePdf}
                                className="text-primary underline underline-offset-4 hover:no-underline"
                              >
                                PDF
                              </a>
                            )}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {credits && credits.entries.length === 0 && (
                    <tr className="border-t">
                      <td
                        colSpan={7}
                        className="px-3 py-6 text-center text-sm text-muted-foreground"
                      >
                        No payments or grants for this org yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {credits && credits.total > LEDGER_PAGE_SIZE && (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span className="tabular-nums">
                  {credits.offset + 1}
                  {"–"}
                  {Math.min(credits.offset + credits.entries.length, credits.total)} of{" "}
                  {credits.total}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCreditsOffset((o) => Math.max(0, o - LEDGER_PAGE_SIZE))}
                    disabled={credits.offset === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCreditsOffset((o) => o + LEDGER_PAGE_SIZE)}
                    disabled={credits.offset + credits.entries.length >= credits.total}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      <section className="space-y-3">
        <h3 className="font-medium">
          Ledger{ledger ? ` (${ledger.total})` : ""}
        </h3>
        {ledgerIsError ? (
          <InlineError error={ledgerError ?? "Failed to load ledger"} />
        ) : (
          <>
            <div className="rounded-md border overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Description</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-right">Balance after</th>
                  </tr>
                </thead>
                <tbody>
                  {(ledger?.entries ?? []).map((row) => (
                    <tr key={row.id} className="border-t">
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                        {formatDate(row.createdAt as unknown as string)}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="secondary">{row.type}</Badge>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {row.description ?? "\u2014"}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums ${
                          row.amount < 0 ? "text-destructive" : ""
                        }`}
                      >
                        {row.amount > 0 ? "+" : ""}
                        {formatCostLong(row.amount)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatCostLong(row.balanceAfter)}
                      </td>
                    </tr>
                  ))}
                  {ledger && ledger.entries.length === 0 && (
                    <tr className="border-t">
                      <td
                        colSpan={5}
                        className="px-3 py-6 text-center text-sm text-muted-foreground"
                      >
                        No ledger entries for this org.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {ledger && ledger.total > 0 && (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span className="tabular-nums">
                  {ledger.offset + 1}
                  {"\u2013"}
                  {Math.min(ledger.offset + ledger.entries.length, ledger.total)} of{" "}
                  {ledger.total}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setLedgerOffset((o) => Math.max(0, o - LEDGER_PAGE_SIZE))
                    }
                    disabled={ledger.offset === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setLedgerOffset((o) => o + LEDGER_PAGE_SIZE)}
                    disabled={ledger.offset + ledger.entries.length >= ledger.total}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function WindowMetric({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-md border p-4">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}
